import './bottom-nav.css'

import { useModal } from '@ebay/nice-modal-react'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { type ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import casino_dark_after from '../../assets/common/casino_active.webp'
import casino_light_after from '../../assets/common/casino_light_active.webp'
import casino_light_before from '../../assets/common/casino_light_off.webp'
import casino_dark_before from '../../assets/common/casino_off.webp'
import deportes_dark_after from '../../assets/common/deportes_active.webp'
import deportes_light_after from '../../assets/common/deportes_light_active.webp'
import deportes_light_before from '../../assets/common/deportes_light_off.webp'
import deportes_dark_before from '../../assets/common/deportes_off.webp'
import maximize_light from '../../assets/common/expand_active.webp'
import minimize_light from '../../assets/common/expand_off.webp'
import favoritos_dark_after from '../../assets/common/favoritos_active.webp'
import favoritos_light_after from '../../assets/common/favoritos_light_active.webp'
import favoritos_light_before from '../../assets/common/favoritos_light_off.webp'
import favoritos_dark_before from '../../assets/common/favoritos_off.webp'
import maximize_dark from '../../assets/common/maximize.webp'
import minimize_dark from '../../assets/common/minimize.webp'
import recently_dark_after from '../../assets/common/recently_active.webp'
import recently_light_after from '../../assets/common/recently_light_active.webp'
import recently_light_before from '../../assets/common/recently_light_off.webp'
import recently_dark_before from '../../assets/common/recently_off.webp'
import referral_dark_after from '../../assets/common/referral_after.webp'
import referral_dark_before from '../../assets/common/referral_before.webp'
import referral_light_after from '../../assets/common/referral_light_active.webp'
import referral_light_before from '../../assets/common/referral_light_off.webp'
import search_dark_after from '../../assets/common/search_active.webp'
import search_light_after from '../../assets/common/search_light_active.webp'
import search_light_before from '../../assets/common/search_light_off.webp'
import search_dark_before from '../../assets/common/search_off.webp'
import sponsors_dark_after from '../../assets/common/sponsors_active.webp'
import sponsors_light_after from '../../assets/common/sponsors_light_active.webp'
import sponsors_light_before from '../../assets/common/sponsors_light_off.webp'
import sponsors_dark_before from '../../assets/common/sponsors_off.webp'
import { useLoadMoreGames } from '../../hooks/useGames'
import { useBoundStore } from '../../store/store'
import classNames from '../../utils/classNames'
import SearchModal from '../casino/search-modal'
import GlowRing from '../common/glow-ring'

type TabObj = {
  icon: ReactNode
  badge?: ReactNode
  label: string
  link: string
  onLinkClick: undefined | (() => void)
  isProtected: boolean
  // Tab is removed (not just guarded) while no user is logged in.
  guestHidden?: boolean
  // Search params to carry on the link (e.g. the `section=sports` marker).
  search?: Record<string, unknown>
}

const ACTIVE_CLASS =
  '[&_p]:text-highlight-gold [&_svg]:fill-highlight-gold [&_path]:fill-highlight-gold active'
const INACTIVE_CLASS =
  '[&_p]:text-[#888] [&_svg]:fill-[#888] [&_path]:fill-[#888]'

// Compare without trailing slashes so '/profile/referral/' matches
// '/profile/referral'.
const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/'

// The before/after image pair every tab icon is built from. `fullSize`
// stretches the images to the wrapper (casino/sports assets need it).
const TabIcon = ({
  after,
  before,
  alt = '',
  fullSize = false,
}: {
  after: string
  before: string
  alt?: string
  fullSize?: boolean
}) => {
  const imageClass = classNames(
    'absolute dark:w-4/5 dark:h-[unset] aspect-square left-2/4 top-2/4',
    fullSize && 'w-full h-full',
  )
  return (
    <div className="gif relative overflow-hidden w-[30px] h-[30px] mt-[-5px] mr-[-5px] mb-[-5px] ml-[-5px]">
      <LazyLoadImage
        className={classNames('icon-after opacity-0', imageClass)}
        alt={alt}
        src={after}
        style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
      />
      <LazyLoadImage
        className={classNames('icon-before opacity-1', imageClass)}
        alt={alt}
        src={before}
        style={{ transform: 'var(--translate-50-50) rotate(0deg)' }}
      />
    </div>
  )
}

const BottomNav = () => {
  const theme = useBoundStore(state => state.common.theme)
  const username = useBoundStore(state => state.user.username)
  const isFullScreen = useBoundStore(state => state.common.isFullScreen)
  const setIsFullScreen = useBoundStore(state => state.common.setIsFullScreen)
  const loadMoreGames = useLoadMoreGames()
  const navigate = useNavigate()
  const searchModal = useModal(SearchModal)
  const { t } = useTranslation(['drawer', 'casino'])
  // Router location, not the global one: the tab set and the active state have
  // to re-derive on every client-side navigation, including search-param-only
  // ones (the `section=sports` marker below).
  const routerLocation = useLocation()
  const pathname = routerLocation.pathname

  const {
    minimize,
    maximize,
    deportes_after,
    deportes_before,
    casino_after,
    casino_before,
    favoritos_after,
    favoritos_before,
    sponsors_after,
    sponsors_before,
    recently_after,
    recently_before,
    referral_after,
    referral_before,
    search_after,
    search_before,
  } = useMemo(() => {
    return {
      minimize: theme === 'dark' ? minimize_dark : minimize_light,
      maximize: theme === 'dark' ? maximize_dark : maximize_light,
      deportes_after:
        theme === 'dark' ? deportes_dark_after : deportes_light_after,
      deportes_before:
        theme === 'dark' ? deportes_dark_before : deportes_light_before,
      casino_after: theme === 'dark' ? casino_dark_after : casino_light_after,
      casino_before:
        theme === 'dark' ? casino_dark_before : casino_light_before,
      favoritos_after:
        theme === 'dark' ? favoritos_dark_after : favoritos_light_after,
      favoritos_before:
        theme === 'dark' ? favoritos_dark_before : favoritos_light_before,
      sponsors_after:
        theme === 'dark' ? sponsors_dark_after : sponsors_light_after,
      sponsors_before:
        theme === 'dark' ? sponsors_dark_before : sponsors_light_before,
      recently_after:
        theme === 'dark' ? recently_dark_after : recently_light_after,
      recently_before:
        theme === 'dark' ? recently_dark_before : recently_light_before,
      referral_after:
        theme === 'dark' ? referral_dark_after : referral_light_after,
      referral_before:
        theme === 'dark' ? referral_dark_before : referral_light_before,
      search_after: theme === 'dark' ? search_dark_after : search_light_after,
      search_before:
        theme === 'dark' ? search_dark_before : search_light_before,
    }
  }, [theme])

  // The referral page has no section of its own, so it carries a
  // `?section=sports` marker to stay in the sports set once reached from there.
  // Both halves are matched narrowly: the path tests read the pathname only, so
  // a search value containing '/sports' (an affiliate or redirect target) can't
  // flip the set, and the marker is read as a parsed param rather than a
  // substring, so neither `?redirect=section=sports` nor `?section=sportsball`
  // can either.
  const isSportsSection =
    pathname.includes('/sports') ||
    pathname.includes('/sponsors') ||
    new URLSearchParams(routerLocation.searchStr).get('section') === 'sports'

  // Game page (/casino/<category>/<game>) — matched on pathname only, the same
  // test root.tsx uses to hide the global nav. game.tsx renders its own
  // <BottomNav /> here, which gets the casino game set below.
  const isGamePage = /\/casino\/.+\/.+/.test(pathname)

  // Each tab is defined once; the per-section sets below compose them.
  const casinoTab: TabObj = {
    icon: <TabIcon after={casino_after} before={casino_before} fullSize />,
    label: t('casino'),
    link: `/casino/inicio`,
    onLinkClick: () => {
      loadMoreGames('15648', '', true)
    },
    isProtected: false,
  }

  const sportsTab: TabObj = {
    icon: <TabIcon after={deportes_after} before={deportes_before} fullSize />,
    label: t('sports'),
    link: '/sports',
    onLinkClick: undefined,
    isProtected: false,
  }

  // A single leading tab that offers the section you are not in, rather than
  // spending two slots on casino and sports at once. The casino tab has never
  // preloaded games from the sports section, so that variant drops the
  // preload.
  const sportsCasinoToggle: TabObj = isSportsSection
    ? { ...casinoTab, onLinkClick: undefined }
    : sportsTab

  const inviteTab: TabObj = {
    icon: <TabIcon after={referral_after} before={referral_before} />,
    label: t('invite'),
    link: '/profile/referral/',
    onLinkClick: undefined,
    isProtected: true,
    // Carry the marker out of the sports section so the referral page keeps
    // the sports tab set instead of falling back to the casino one.
    search: isSportsSection ? { section: 'sports' } : undefined,
  }

  const favoritosTab: TabObj = {
    icon: <TabIcon after={favoritos_after} before={favoritos_before} />,
    label: t('casino:favorites'),
    link: '/casino/favoritos',
    onLinkClick: undefined,
    isProtected: true,
    guestHidden: true,
  }

  const recientesTab: TabObj = {
    icon: <TabIcon after={recently_after} before={recently_before} />,
    label: t('recent'),
    link: '/casino/recientes',
    onLinkClick: () => {
      loadMoreGames('recent_played', '', true)
    },
    isProtected: true,
    guestHidden: true,
  }

  const sponsorsTab: TabObj = {
    icon: <TabIcon after={sponsors_after} before={sponsors_before} />,
    label: 'SPONSORS',
    link: '/sponsors',
    onLinkClick: undefined,
    isProtected: false,
  }

  const searchTab: TabObj = {
    icon: (
      <TabIcon
        after={search_after}
        before={search_before}
        alt="Casino Search"
      />
    ),
    label: t('casino:search-category'),
    link: '',
    onLinkClick: () => {
      searchModal.show()
    },
    isProtected: true,
  }

  // The section toggle always leads and invite always trails. The sports
  // section swaps favourites/recents for the sponsors tab; every other page
  // keeps favourites + recents.
  const sportTabs: TabObj[] = [sportsCasinoToggle, sponsorsTab, inviteTab]

  const casinoTabs: TabObj[] = [
    sportsCasinoToggle,
    favoritosTab,
    recientesTab,
    inviteTab,
  ]

  // Rendered by the game page's own <BottomNav /> (game.tsx) — root.tsx hides
  // the global one there. Casino home rather than the toggle, since the toggle
  // is section-relative and would offer sports here.
  const casinoGameTabs: TabObj[] = [
    casinoTab,
    recientesTab,
    favoritosTab,
    searchTab,
  ]

  const sectionTabs = isGamePage
    ? casinoGameTabs
    : isSportsSection
    ? sportTabs
    : casinoTabs

  // Favourites and recents are only offered to logged-in players.
  const tabs: TabObj[] = useMemo(
    () => sectionTabs.filter(tab => username || !tab.guestHidden),
    [sectionTabs, username],
  )

  return (
    <div
      className={classNames(
        // `game-card-glow` animates a registered custom property feeding a
        // conic-gradient, which cannot be composited, so its ::before repaints
        // every frame — and this element also carries `backdrop-blur-md`, whose
        // filter may re-run alongside it over a nearly full-width strip. Kept
        // deliberately: that cost is one always-on-screen ring, not the page of
        // tiles the casino grid was paying it for. If it does show up on low-end
        // Android, the fix is to lift the ring onto its own element so it stops
        // sharing a box with the blur — not to drop the animation again.
        'game-card-glow !fixed z-[120] bg-[rgba(255,255,255,0.75)] dark:bg-[rgba(10,10,10,0.75)] backdrop-blur-md rounded-full w-[calc(100%-32px)]',
        'left-4 grid h-[60px] items-center transition-all duration-[0.2s] ease-[ease-in-out] px-3 py-0',
        'border border-solid border-[color-mix(in_srgb,var(--glowColorSecondary)_25%,transparent)] dark:border-[color-mix(in_srgb,var(--highlight-gold)_35%,transparent)]',
        'shadow-[0px_-2px_8px_color-mix(in_srgb,var(--glowColorSecondary)_25%,transparent)] dark:shadow-[0_0_16px_color-mix(in_srgb,var(--highlight-gold)_40%,transparent)]',
        // The docked offset carries the bottom safe-area inset, so it lives in
        // bottom-nav.css — env() can't survive an inline style.
        isFullScreen ? 'bottom-nav-hidden' : 'bottom-nav-docked',
      )}
      style={{
        gridTemplateColumns: `repeat(${tabs.length + 1},1fr)`,
      }}>
      {/* Absolutely positioned, so it takes no grid cell.
          The spinning square only has to cover the host's diagonal, and this
          host is a wide, short pill — sqrt(w^2 + h^2) is barely over its
          width, so the 142% default (sized for square game tiles) rasterised
          about twice the pixels needed for a permanently-composited layer. */}
      <GlowRing className="[--glow-ring-size:110%]" />
      {tabs.map(tab => {
        const guestGuarded = tab.isProtected && !username

        // Button-rendered tabs (empty link, e.g. search) don't use the active
        // state, so bail out before computing it.
        if (!tab.link) {
          return (
            <button
              key={tab.label}
              onClick={() => {
                if (guestGuarded) {
                  navigate({ to: '/register' })
                  return
                }
                tab.onLinkClick?.()
              }}>
              <div
                className={classNames(
                  'tab flex flex-col items-center relative',
                  '[&_svg]:w-[25px] [&_svg]:transition-all [&_svg]:duration-[0.2s] [&_svg]:ease-[ease-in-out]',
                )}>
                <div className="w-[30px] h-[30px] flex justify-center items-center transition-all duration-[0.2s] ease-[ease-in-out] relative rounded-[50%]">
                  {tab.icon}
                </div>
                <p className="text-[10px] font-bold uppercase text-center">
                  {tab.label}
                </p>
              </div>
            </button>
          )
        }

        const isCasinoHomeTab = tab.link.includes('/casino/inicio')
        const isActive = isCasinoHomeTab
          ? (pathname === '/' || pathname.includes('/casino')) &&
            !pathname.includes('favoritos') &&
            !pathname.includes('recientes')
          : normalizePath(pathname) === normalizePath(tab.link)

        return (
          <Link
            // `to={undefined}` resolves to the current route and swallows the
            // tap, so guests are linked straight to the register page instead.
            to={guestGuarded ? '/register' : tab.link}
            key={tab.label}
            onClick={guestGuarded ? undefined : tab.onLinkClick}
            search={tab.search ?? {}}
            params={{}}>
            <div
              className={classNames(
                'tab flex flex-col items-center relative',
                '[&_svg]:w-[25px] [&_svg]:transition-all [&_svg]:duration-[0.2s] [&_svg]:ease-[ease-in-out]',
                isActive ? ACTIVE_CLASS : INACTIVE_CLASS,
              )}>
              {tab.badge}
              <div className="w-[30px] h-[30px] flex justify-center items-center transition-all duration-[0.2s] ease-[ease-in-out] relative rounded-[50%]">
                {tab.icon}
              </div>
              <p className="text-[10px] font-bold uppercase text-center">
                {tab.label}
              </p>
            </div>
          </Link>
        )
      })}
      <div
        className={classNames(
          'tab flex flex-col items-center transition-all duration-[0.2s] ease-[ease-in-out] right-3',
          isFullScreen ? 'absolute bottom-[68px]' : 'bottom-2',
        )}
        onClick={() => setIsFullScreen(!isFullScreen)}>
        <div className="w-[30px] h-[30px] flex justify-center items-center transition-all duration-[0.2s] ease-[ease-in-out] relative rounded-[50%]">
          <img
            className={classNames(
              'w-[25px] transition-all duration-[0.2s] ease-[ease-in-out]',
              isFullScreen && 'w-[40px]',
            )}
            src={isFullScreen ? minimize : maximize}
            alt=""
            style={{ rotate: `${isFullScreen ? 0 : 180}deg` }}
          />
        </div>
        {!isFullScreen && (
          <p className="text-[10px] font-bold uppercase text-center">
            {t('expand')}
          </p>
        )}
      </div>
    </div>
  )
}

export default BottomNav
